import { Action, createReducer, on } from '@ngrx/store';

import { IMemberDetailsState } from './member-details.model';
import * as memberDetailsActions from './member-details.actions';
import * as memberDetailsUtlities from './member-details.utility';

export const initialState: IMemberDetailsState = {
  member: null,
  eligibility: null,
  coverages: [],
  displayedCoverages: [],
  coverageOptions: [],
  otherCoverages: [],
  displayedOtherCoverages: [],
  pcpHistory: {
    current: null,
    imputed: null,
    historic: []
  },
  delegatedAccessToken: null,
  selectedCoverageItem: null,
  associatedMemberIds: [],
  subscription: null,
  additionalDelegatedAccessTokens: null,
  additionalSubscriptions: [],
  coverageFilter: 'active',
  cob: null,
  hixCodes: null,
  associatedFamilies: null,
  loading: false,
  documents: {
    error: false,
    loading: false,
    documents: []
  },
  spendingSummary: {
    loading: false,
    error: false,
    spendingSummary: null
  },
  spendingAccounts: [],
  careManagementDocuments: {
    loading: false,
    error: false,
    careManagementDocuments: []
  },
  getSubsError: null,
  loadingSubData: false,
  snapsRenewalDates: {
    maRenewalDate: '',
    snapRenewalDate: ''
  },
  memberNetworks: {
    status: null,
    data: null
  },
  providerDetails: {
    loading: false,
    error: false,
    providerDetails: null
  }
};

const reducer = createReducer(
  initialState,
  on(memberDetailsActions.getMember, (state) => ({
    ...state,
    coverageOptions: [],
    loading: true
  })),
  on(memberDetailsActions.getMemberSuccess, (state, { member }) => ({
    ...state,
    member,
    loading: false
  })),
  on(memberDetailsActions.getMemberFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false
  })),
  on(memberDetailsActions.getSubscription, (state) => ({
    ...state,
    subscription: null,
    loadingSubData: true
  })),
  on(memberDetailsActions.getSubscriptionSuccess, (state, { subscription }) => {
    const coverages =
      subscription?.subscriber?.eligibilities.map((coverage) => memberDetailsUtlities.mapCoverageResponse(coverage)) ||
      [];
    const newCoverages = state?.coverages?.length ? [...state?.coverages, ...coverages] : coverages;
    const sub = { ...state.subscription };
    sub.subscriber = {
      ...subscription.subscriber,
      ...sub.subscriber
    };
    sub.families = subscription.families;
    return {
      ...state,
      subscription: sub,
      coverages: newCoverages,
      displayedCoverages: memberDetailsUtlities.filterCoverages(state?.coverageFilter, newCoverages),
      loadingSubData: false
    };
  }),
  on(memberDetailsActions.getSubscriptionFailure, (state, { error }) => ({
    ...state,
    getSubsError: error,
    loadingSubData: false
  })),
  on(memberDetailsActions.getAssociatedMemberIds, (state) => ({
    ...state,
    associatedMemberIds: []
  })),
  on(memberDetailsActions.getAssociatedMemberIdsSuccess, (state, { associatedMemberIds }) => {
    const newOtherCoverages = state?.otherCoverages?.length
      ? memberDetailsUtlities.convertPrimaryCoverages(state?.otherCoverages, associatedMemberIds)
      : null;
    return {
      ...state,
      associatedMemberIds,
      otherCoverages: newOtherCoverages,
      displayedOtherCoverages: memberDetailsUtlities.filterCoverages(state?.coverageFilter, newOtherCoverages)
    };
  }),
  on(memberDetailsActions.getAssociatedMemberIdsFailure, (state, { error }) => ({
    ...state,
    error
  })),
  on(memberDetailsActions.getPCPHistory, (state) => ({
    ...state,
    eligibility: null
  })),
  on(memberDetailsActions.getPCPHistorySuccess, (state, { pcpHistory }) => ({
    ...state,
    pcpHistory
  })),
  on(memberDetailsActions.getPCPHistoryFailure, (state, { error }) => ({
    ...state,
    error
  })),
  on(memberDetailsActions.getCOB, (state) => ({
    ...state,
    otherCoverages: []
  })),
  on(memberDetailsActions.getCOBSuccess, (state, { cob }) => {
    const newOtherCoverages = state?.associatedMemberIds?.length
      ? memberDetailsUtlities.convertPrimaryCoverages(cob, state?.associatedMemberIds)
      : cob;
    return {
      ...state,
      otherCoverages: newOtherCoverages,
      displayedOtherCoverages: memberDetailsUtlities.filterCoverages(state?.coverageFilter, newOtherCoverages)
    };
  }),
  on(memberDetailsActions.getCOBFailure, (state, { error }) => ({
    ...state,
    error
  })),
  on(memberDetailsActions.getAssociatedFamily, (state) => ({
    ...state,
    associatedFamilies: null,
    loading: true
  })),
  on(memberDetailsActions.getAssociatedFamilySuccess, (state, { familyMembers }) => ({
    ...state,
    associatedFamilies: familyMembers,
    loading: false
  })),
  on(memberDetailsActions.getAssociatedFamilyFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false
  })),
  on(memberDetailsActions.getDocuments, (state) => ({
    ...state,
    documents: {
      error: false,
      loading: true,
      documents: []
    }
  })),
  on(memberDetailsActions.getDocumentsSuccess, (state, { documents }) => ({
    ...state,
    documents: {
      error: false,
      loading: false,
      documents
    }
  })),
  on(memberDetailsActions.getDocumentsFailure, (state, { error }) => ({
    ...state,
    documents: {
      error: true,
      loading: false,
      documents: []
    }
  })),
  on(memberDetailsActions.getHixCodesSuccess, (state, { hixCodes }) => {
    let updatedCoverages = [...state?.coverages];
    if (updatedCoverages?.length) {
      updatedCoverages = memberDetailsUtlities.addHixStatusesToCoverages(state?.coverages, hixCodes);
    }
    return {
      ...state,
      hixCodes,
      coverages: updatedCoverages,
      displayedCoverages: memberDetailsUtlities.filterCoverages(state?.coverageFilter, updatedCoverages)
    };
  }),
  on(memberDetailsActions.getHixCodesFailure, (state, { error }) => ({
    ...state,
    error
  })),
  on(memberDetailsActions.updateCoverageFilter, (state, { status }) => ({
    ...state,
    coverageFilter: status,
    displayedCoverages: memberDetailsUtlities.filterCoverages(status, state?.coverages),
    displayedOtherCoverages: memberDetailsUtlities.filterCoverages(status, state?.otherCoverages)
  })),
  on(memberDetailsActions.getDelegatedAccessTokenSuccess, (state, { delegatedAccessToken }) => ({
    ...state,
    delegatedAccessToken
  })),
  on(memberDetailsActions.removeDelegatedAccessToken, (state) => ({
    ...state,
    delegatedAccessToken: null
  })),
  on(memberDetailsActions.selectCoverageItem, (state, { coverage }) => ({
    ...state,
    selectedCoverageItem: coverage,
    loading: false
  })),
  on(memberDetailsActions.clearMemberDetails, (state) => ({
    ...initialState
  })),
  on(memberDetailsActions.fetchSpendingSummary, (state) => ({
    ...state,
    spendingSummary: { error: false, loading: true, spendingSummary: null }
  })),
  on(memberDetailsActions.fetchSpendingSummarySuccess, (state, { spendingSummary }) => ({
    ...state,
    spendingSummary: { error: false, loading: false, spendingSummary }
  })),
  on(memberDetailsActions.fetchSpendingSummaryError, (state) => ({
    ...state,
    spendingSummary: { error: true, loading: false, spendingSummary: null }
  })),
  on(memberDetailsActions.fetchSpendingAccountsSuccess, (state, { spendingAccounts }) => ({
    ...state,
    spendingAccounts
  })),
  on(memberDetailsActions.resetSpendingAccounts, (state) => ({
    ...state,
    spendingAccounts: []
  })),
  on(memberDetailsActions.getCareManagementDocuments, (state) => ({
    ...state,
    careManagementDocuments: {
      careManagementDocuments: [],
      loading: true,
      error: false
    }
  })),
  on(memberDetailsActions.getCareManagementDocumentsSuccess, (state, { careManagementDocuments }) => ({
    ...state,
    careManagementDocuments: {
      careManagementDocuments,
      loading: false,
      error: false
    }
  })),
  on(memberDetailsActions.getCareManagementDocumentsFailure, (state) => ({
    ...state,
    careManagementDocuments: {
      careManagementDocuments: [],
      loading: false,
      error: true
    }
  })),
  on(memberDetailsActions.fetchMemberPreferencesSuccess, (state, { preferences }) => ({
    ...state,
    subscription: {
      ...state?.subscription,
      subscriber: {
        ...state?.subscription?.subscriber,
        preferences
      }
    }
  })),
  on(memberDetailsActions.fetchMemberPreferencesFailure, (state) => ({
    ...state,
    subscription: {
      ...state?.subscription,
      subscriber: {
        ...state?.subscription?.subscriber,
        preferences: null,
      }
    }
  })),
  on(memberDetailsActions.getSnapsRenewalDates, (state, { payload }) => ({
    ...state,
    snapsRenewalDates: payload
  })),
  on(memberDetailsActions.getMemberNetworks, (state) => ({
    ...state,
    memberNetworks: {
      status: null,
      data: null
    },
    providerDetails: {
      error: false,
      loading: false,
      providerDetails: null
    }
  })),
  on(memberDetailsActions.setMemberNetworks, (state, { memberNetworks, status }) => ({
    ...state,
    memberNetworks: {
      status, data:
        memberNetworks
    }
  })),
  on(memberDetailsActions.getProviderDetails, (state) => ({
    ...state,
    providerDetails: {
      error: false,
      loading: true,
      providerDetails: null
    }
  })),
  on(memberDetailsActions.setProviderDetails, (state, { providerDetails }) => ({
    ...state,
    providerDetails: {
      error: false,
      loading: false,
      providerDetails: providerDetails
    }
  })),
  on(memberDetailsActions.setProviderDetailsFailure, (state) => ({
    ...state,
    providerDetails: {
      error: true,
      loading: false,
      providerDetails: null
    }
  }))
);

export function memberDetailsReducer(state: IMemberDetailsState | undefined, action: Action): IMemberDetailsState {
  return reducer(state, action);
}
